/**
 * #10426-a1 (MST-6431) — exclusive_play timeout → shared fallback, with a double-play guard.
 *
 * Before: any "ipc invoke timeout" / "Command timeout" kept exclusive (#10453) even when nothing
 * was playing — the SMSL on macOS 27 retried exclusive forever with no sound.
 * Now: if the exclusive engine is actually playing after the timeout (the #10453 Mojo2 case — a
 * slow but successful open), keep exclusive. Otherwise abandon the in-flight exclusive dispatch
 * and let the caller play shared (with an on-screen notice).
 *
 * Double-play guard — SHARED WINS. Once a dispatch is abandoned, a late exclusive_play success
 * (old Connectors ≤ v11.0.327 can resolve minutes later) is stopped with exclusive_stop and never
 * starts the exclusive poller. The listener was already told "Shared — not bit-perfect", and
 * switching mid-track would restart the song from 0 on top of the shared stream.
 */
import { useMaestroStore } from '../store/maestroStore';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { isExclusiveOpenTimeoutError, sleep } from '../utils/exclusiveFallbackTiming';
import { markStickySharedFallback } from '../utils/stickySharedFallback';

/** Cap for the post-timeout engine probe — the IPC that just timed out may be wedged too. */
export const EXCLUSIVE_TIMEOUT_ENGINE_PROBE_MS = 1500;
/** A dispatch-side probe result is reused by the queue verify + fallback decision within this window. */
const TIMEOUT_PROBE_REUSE_MS = 30_000;

/** superseded: a newer play replaced this one (queue flush, track changed, newer dispatch) — never play the old track shared. */
export type ExclusiveTimeoutDecision = 'not_timeout' | 'keep_exclusive' | 'fallback_shared' | 'superseded';

let dispatchGen = 0;
let abandonedGen = 0;
let lastTimeoutProbe: { gen: number; enginePlaying: boolean; at: number } | null = null;

/** Called by dispatchExclusivePlayUri BEFORE its pre-stops, so a not-yet-sent dispatch is never missed. */
export function beginExclusiveDispatch(): number {
  dispatchGen += 1;
  return dispatchGen;
}

export function isLatestExclusiveDispatch(gen: number): boolean {
  return gen === dispatchGen;
}

export function isExclusiveDispatchAbandoned(gen: number): boolean {
  return gen <= abandonedGen;
}

async function stopAbandonedExclusive(source: string): Promise<void> {
  try {
    const { exclusiveStop } = await import('../lib/connector');
    await exclusiveStop();
  } catch (err) {
    console.warn('[exclusive] stop after abandon failed:', source, err);
  }
}

/** Mark exclusive dispatch `gen` (default: latest) abandoned; stop it on the Connector if nothing newer started. */
export function abandonInFlightExclusiveDispatch(gen: number = dispatchGen): void {
  abandonedGen = Math.max(abandonedGen, gen);
  if (gen === dispatchGen) void stopAbandonedExclusive('abandon');
}

/** Dispatch side: exclusive_play resolved. True when the dispatch was abandoned (caller must not start exclusive). */
export async function handleExclusivePlayResolvedAfterAbandon(gen: number): Promise<boolean> {
  if (!isExclusiveDispatchAbandoned(gen)) return false;
  const latest = isLatestExclusiveDispatch(gen);
  diagnosticLog('warn', 'transport', 'exclusive_play resolved after shared fallback — shared kept', {
    source: 'dispatch',
    stoppedExclusive: latest,
  });
  if (latest) await stopAbandonedExclusive('dispatch_late_resolve');
  return true;
}

/** connector.ts hook: an exclusive_play invoke resolved after its IPC timeout already fired. */
export function onLateExclusivePlayIpcResolve(): void {
  if (dispatchGen === 0 || abandonedGen !== dispatchGen) return;
  diagnosticLog('warn', 'transport', 'exclusive_play resolved after shared fallback — shared kept', {
    source: 'ipc_late_resolve',
    stoppedExclusive: true,
  });
  void stopAbandonedExclusive('ipc_late_resolve');
}

async function probeExclusiveEnginePlaying(): Promise<boolean> {
  const { verifyExclusiveEnginePlaying } = await import('./localNativePlaybackVerify');
  return Promise.race([
    verifyExclusiveEnginePlaying().catch(() => false),
    sleep(EXCLUSIVE_TIMEOUT_ENGINE_PROBE_MS).then(() => false),
  ]);
}

function activeZone() {
  return useMaestroStore.getState().discovery?.activeZone as Parameters<typeof markStickySharedFallback>[0];
}

/**
 * Dispatch side, inside the playUri queue command: exclusive_play timed out. Probe the engine once
 * (capped) and, when it is not playing, mark the device sticky-shared BEFORE the command settles —
 * so a queued next/skip already routes straight to shared instead of starting another exclusive timer.
 */
export async function noteExclusiveOpenTimeout(gen: number, errMsg: string, track?: unknown): Promise<void> {
  if (!isExclusiveOpenTimeoutError(errMsg)) return;
  const enginePlaying = await probeExclusiveEnginePlaying();
  lastTimeoutProbe = { gen, enginePlaying, at: Date.now() };
  if (!enginePlaying) markStickySharedFallback(activeZone(), 'exclusive_timeout', track);
}

/** Engine state probed right after the latest dispatch timed out, or null when there is none. */
export function recentExclusiveTimeoutProbe(): boolean | null {
  const p = lastTimeoutProbe;
  if (!p || p.gen !== dispatchGen || Date.now() - p.at > TIMEOUT_PROBE_REUSE_MS) return null;
  return p.enginePlaying;
}

function trackChangedSince(trackId: unknown): boolean {
  const current = useMaestroStore.getState().currentTrack?.id;
  return trackId != null && current != null && String(current) !== String(trackId);
}

/** Decide what a failed exclusive play means: not a timeout, a slow success, superseded, or fall back to shared. */
export async function decideExclusiveTimeout(
  errMsg: string,
  branch: string,
  trackId?: unknown,
): Promise<ExclusiveTimeoutDecision> {
  if (/flushed by playuri/i.test(String(errMsg || ''))) {
    diagnosticLog('info', 'playback', 'exclusive play superseded — no shared fallback', { branch, error: errMsg });
    return 'superseded';
  }
  if (!isExclusiveOpenTimeoutError(errMsg)) return 'not_timeout';
  const probed = lastTimeoutProbe && Date.now() - lastTimeoutProbe.at <= TIMEOUT_PROBE_REUSE_MS ? lastTimeoutProbe : null;
  const gen = probed ? probed.gen : dispatchGen; // the dispatch that actually timed out
  if (probed ? probed.enginePlaying : await probeExclusiveEnginePlaying()) {
    diagnosticLog('info', 'playback', 'exclusive command timeout — engine playing, keeping exclusive', { branch, error: errMsg });
    return 'keep_exclusive';
  }
  markStickySharedFallback(activeZone(), 'exclusive_timeout', useMaestroStore.getState().currentTrack); // once per session; covers queue "Command timeout"
  if (gen !== dispatchGen || trackChangedSince(trackId)) {
    abandonInFlightExclusiveDispatch(gen);
    diagnosticLog('info', 'playback', 'exclusive play superseded — no shared fallback', {
      branch, error: errMsg, trackId, currentTrackId: useMaestroStore.getState().currentTrack?.id, newerDispatch: gen !== dispatchGen,
    });
    return 'superseded';
  }
  abandonInFlightExclusiveDispatch(gen);
  diagnosticLog('warn', 'playback', 'exclusive open timeout — falling back to shared', { branch, error: errMsg });
  return 'fallback_shared';
}

/** Test-only reset. */
export function __resetExclusiveTimeoutFallbackForTests(): void {
  dispatchGen = 0;
  abandonedGen = 0;
  lastTimeoutProbe = null;
}
